import { useForms } from '@/corePagesModule/composables/useForm';
import { formOptionsBusiness, validateRuleKeyMap } from '../constants';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { computed, ref } from 'vue';
import { useUpdateFieldState } from './useUpdateFieldState';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { UpdateBusinessViewModel } from '../UpdateBusinessViewModel';

/**
 * UpdateBaseBusinessFormを使用するためのComposable
 * @param option - オプション
 */
export const useUpdateBaseBusinessForm = (option?: { validateRuleKeyMap?: Record<string, ValidateRule[]> }) => {
  const updateBusinessVM = new UpdateBusinessViewModel();
  const isEdited = ref<boolean>(false);
  const currentOpenSelectInputName = ref('');

  const fieldItems = computed((): FieldItem[] => {
    const targetArray = formOptionsBusiness;
    const items = targetArray.map((name: string) => {
      return {
        name,
        rules: option?.validateRuleKeyMap?.[name] ?? validateRuleKeyMap[name],
      };
    });
    return items;
  });

  const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);
  const { addNewOption, updateValue, updateUserValue } = useUpdateFieldState(fieldState, isEdited);

  /**
   * フォームを初期化する
   * @param item - アイテム
   * @param currentItem - 現在のアイテム
   */
  const initializeForm = (item: Partial<IBusiness>, currentItem?: Partial<IBusiness>) => {
    resetForm(fieldItems.value);
    for (let i = 0; i < fieldItems.value.length; i++) {
      const keyName = fieldItems.value[i].name;
      if (keyName === 'baseBusinessResponsibleUser') {
        fieldState[keyName].setValue(
          currentItem?.baseBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
            item.baseBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
            '',
          false
        );
      } else {
        fieldState[keyName].setValue(currentItem?.[keyName] ?? item[keyName] ?? '', false);
      }
    }
  };

  /**
   * 変更した取引情報と元々の取引情報をマージしたIBusinessを返す
   * @param baseItem - 元の取引
   */
  const createUpdatedBaseBusiness = (baseItem?: Partial<IBusiness>): IBusiness => {
    const baseBusinessResponsibleUsersString = fieldState.baseBusinessResponsibleUser.value.value;
    return BusinessFactory.create({
      ...baseItem,
      customerNumber: fieldState.customerNumber.value.value,
      customerName: fieldState.customerName.value.value,
      salesDestinationNumber: fieldState.salesDestinationNumber.value.value,
      salesDestinationName: fieldState.salesDestinationName.value.value,
      endUser: updateBusinessVM.convertUnassingedToUndefined(fieldState.endUser.value.value),
      salesUnitPrice: Number(fieldState['salesUnitPrice'].value.value) || undefined,
      baseBusinessResponsibleUsers:
        baseBusinessResponsibleUsersString.length > 0
          ? baseBusinessResponsibleUsersString.split(',').map((userId) => UserAccountFactory.create({ email: userId }))
          : [],
    });
  };

  /**
   * 変更した取引情報を返す
   */
  const createBaseBusinessPatch = (): Partial<IBusiness> => ({
    customerNumber: fieldState.customerNumber.value.value || undefined,
    customerName: fieldState.customerName.value.value || undefined,
    salesDestinationNumber: fieldState.salesDestinationNumber.value.value || undefined,
    salesDestinationName: fieldState.salesDestinationName.value.value || undefined,
    endUser: fieldState.endUser.value.value || undefined,
    salesUnitPrice: Number(fieldState['salesUnitPrice'].value.value) || undefined,
    baseBusinessResponsibleUsers:
      fieldState.baseBusinessResponsibleUser.value.value.length > 0
        ? fieldState.baseBusinessResponsibleUser.value.value.split(',').map((userId) => UserAccountFactory.create({ email: userId }))
        : [],
  });

  return {
    isEdited,
    fieldState,
    currentOpenSelectInputName,
    allValidate,
    createUpdatedBaseBusiness,
    updateValue,
    updateUserValue,
    addNewOption,
    initializeForm,
    createBaseBusinessPatch,
  };
};
